import * as React from "react"
import { router } from "@inertiajs/react"
import { Sparkles } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { InlineField } from "@/components/ui/inline-edit"
import { Input } from "@/components/ui/input"
import { RichTextField } from "@/components/ui/rich-text-field"
import { Select } from "@/components/ui/select"
import { useInlineRecord } from "@/hooks/use-inline-record"
import { postJson } from "@/lib/admin-fetch"
import { inlinePatch } from "@/lib/inline-save"
import { PhotoPicker } from "./PhotoPicker"
import type { FieldsEntry, Lang, LibraryPhoto, SettingCondition, SettingValue } from "./types"

// One section's editable state, flattened into named keys so each box can
// commit on its own: "goal", "en:heading", "es:heading", "setting:slides".
// The prefixes are what the save function reads to decide which half of the
// section a tick belongs to.
const EN = "en:"
const ES = "es:"
const SETTING = "setting:"

export function SectionEditor({
  entry,
  library,
  lang = "en",
  aiEnabled = false,
}: {
  entry: FieldsEntry
  library: LibraryPhoto[]
  lang?: Lang
  aiEnabled?: boolean
}) {
  const [drafting, setDrafting] = React.useState(false)
  const [translating, setTranslating] = React.useState(false)
  const [queueing, setQueueing] = React.useState(false)
  // Cleared locally once "update now" has produced a draft, so the choice
  // disappears without waiting for a save round-trip.
  const [goalActioned, setGoalActioned] = React.useState(false)
  const [aiError, setAiError] = React.useState<string | null>(null)

  // The props are the committed state: every inline save resolves after
  // Inertia has replaced them, so a tick can send the section as the server
  // holds it with one key swapped and never carry a neighbour's half-finished
  // edit along with it.
  const record: Record<string, string> = {
    goal: entry.goal ?? "",
    ...Object.fromEntries(entry.fields.map((f) => [ EN + f.key, entry.content_en[f.key] ?? "" ])),
    ...Object.fromEntries(entry.fields.map((f) => [ ES + f.key, entry.content_es[f.key] ?? "" ])),
    ...Object.fromEntries(
      (entry.setting_defs ?? []).map((def) => [
        SETTING + def.key,
        String(entry.settings?.[def.key] ?? def.default),
      ]),
    ),
  }

  function saveKey(key: string, value: unknown) {
    const payload: {
      goal: string
      content_en: Record<string, string>
      content_es: Record<string, string>
      settings: Record<string, SettingValue>
    } = {
      goal: entry.goal ?? "",
      content_en: { ...entry.content_en },
      content_es: { ...entry.content_es },
      settings: { ...(entry.settings ?? {}) },
    }

    if (key === "goal") payload.goal = String(value)
    else if (key.startsWith(EN)) payload.content_en[key.slice(EN.length)] = String(value)
    else if (key.startsWith(ES)) payload.content_es[key.slice(ES.length)] = String(value)
    else if (key.startsWith(SETTING)) payload.settings[key.slice(SETTING.length)] = String(value)

    return inlinePatch(`/admin/pages/sections/${encodeURIComponent(entry.slug)}`, {
      page_section: payload,
    })
  }

  const fields = useInlineRecord({ record, save: saveKey })

  // A setting's or placement's condition against the *saved* settings — the
  // chooser a hero style unlocks belongs to the live page, so it appears when
  // the change is ticked, not while it is still being considered.
  function settingHolds(condition?: SettingCondition) {
    if (!condition) return true
    const def = entry.setting_defs?.find((d) => d.key === condition.key)
    return String(entry.settings?.[condition.key] ?? def?.default ?? "") === condition.value
  }

  async function draftWithAi() {
    setDrafting(true)
    setAiError(null)
    try {
      const result = await postJson<{ content_en: Record<string, string> }>(
        "/admin/ai/draft-section",
        { slug: entry.slug, goal: fields.value("goal"), content_en: currentEnglish() },
      )
      // The draft is a proposal: it fills the boxes and each one waits for its
      // own tick, so an admin can keep two paragraphs and rewrite the third.
      fields.patch(
        Object.fromEntries(Object.entries(result.content_en).map(([ key, text ]) => [ EN + key, text ])),
      )
      setGoalActioned(true)
    } catch (e) {
      setAiError(e instanceof Error ? e.message : "Drafting failed.")
    } finally {
      setDrafting(false)
    }
  }

  function currentEnglish() {
    return Object.fromEntries(entry.fields.map((f) => [ f.key, fields.value(EN + f.key) ?? "" ]))
  }

  function translate() {
    setTranslating(true)
    setAiError(null)
    router.post(
      "/admin/ai/translate",
      { slug: entry.slug },
      { preserveScroll: true, onFinish: () => setTranslating(false) },
    )
  }

  function queueRefresh() {
    setQueueing(true)
    router.post(
      "/admin/ai/queue-refresh",
      { slug: entry.slug },
      { preserveScroll: true, onFinish: () => setQueueing(false) },
    )
  }

  const prefix = lang === "en" ? EN : ES
  // A section with no copy of its own (the page tiles, a footer background)
  // has nothing to write a goal for, draft or translate — its placements and
  // display settings save themselves.
  const noCopy = entry.fields.length === 0

  return (
    <div className="space-y-6">
      {lang === "en" && !noCopy && (
        <InlineField
          label="Goal — what should this section do? (used when drafting with AI)"
          htmlFor={`${entry.id}-goal`}
          state={fields.state("goal")}
        >
          <Input
            id={`${entry.id}-goal`}
            value={fields.value("goal")}
            placeholder="e.g. Reassure people that online sessions work as well as in person"
            onChange={(e) => fields.set("goal", e.target.value)}
            onKeyDown={fields.state("goal").keys}
          />
        </InlineField>
      )}

      {lang === "en" && entry.goal_dirty && !goalActioned && (
        entry.ai_queued ? (
          <div className="flex flex-wrap items-center gap-3">
            <Badge tone="accent">Queued for tonight's AI update</Badge>
            <p className="text-sm text-ink-muted">
              The nightly sync will redraft this section from its goal and re-mirror the Spanish.
            </p>
          </div>
        ) : (
          <div className="space-y-3 rounded-lg border border-hairline bg-surface p-4">
            <p className="text-sm text-ink-body">
              The goal has changed since this section&apos;s copy was written. Update the copy?
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                size="sm"
                disabled={drafting || !aiEnabled}
                title={aiEnabled ? undefined : "Set ANTHROPIC_API_KEY to enable instant updates"}
                onClick={draftWithAi}
              >
                <Sparkles className="mr-1.5 h-3.5 w-3.5" />
                {drafting ? "Drafting…" : "Update now (uses API credits)"}
              </Button>
              <Button type="button" size="sm" variant="secondary" disabled={queueing} onClick={queueRefresh}>
                {queueing ? "Queueing…" : "Update tonight (included in subscription)"}
              </Button>
            </div>
          </div>
        )
      )}

      {lang === "es" && !noCopy && (
        <div className="flex flex-wrap items-center gap-3">
          {entry.es_stale ? (
            <Badge tone="signal">Spanish out of date</Badge>
          ) : (
            <Badge tone="muted">Spanish up to date</Badge>
          )}
          {aiEnabled && (
            <Button type="button" size="sm" variant="secondary" disabled={translating} onClick={translate}>
              <Sparkles className="mr-1.5 h-3.5 w-3.5" />
              {translating ? "Translating…" : "Translate from English"}
            </Button>
          )}
        </div>
      )}

      {lang === "en" && entry.setting_defs?.length > 0 && (
        <div className="space-y-4 rounded-lg border border-hairline bg-surface p-4">
          <p className="text-sm font-medium text-ink-display">Display settings</p>
          <div className="grid gap-4 sm:grid-cols-2">
            {entry.setting_defs.filter((def) => settingHolds(def.when)).map((def) => {
              const key = SETTING + def.key
              const state = fields.state(key)
              return (
                <InlineField
                  key={def.key}
                  label={def.label}
                  htmlFor={`${entry.id}-setting-${def.key}`}
                  hint={def.help}
                  state={state}
                >
                  {def.type === "number" ? (
                    <Input
                      id={`${entry.id}-setting-${def.key}`}
                      type="number"
                      min={def.min}
                      max={def.max}
                      value={fields.value(key)}
                      onChange={(e) => fields.set(key, e.target.value)}
                      onKeyDown={state.keys}
                    />
                  ) : (
                    <Select
                      id={`${entry.id}-setting-${def.key}`}
                      value={fields.value(key)}
                      onChange={(e) => fields.set(key, e.target.value)}
                      onKeyDown={state.keys}
                    >
                      {def.options.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  )}
                </InlineField>
              )
            })}
          </div>
        </div>
      )}

      {entry.fields.map((field) => {
        const key = prefix + field.key
        const state = fields.state(key)
        return (
          <InlineField
            key={field.key}
            label={`${field.label} ${lang === "en" ? "(English)" : "(Spanish)"}`}
            htmlFor={`${entry.id}-${field.key}-${lang}`}
            state={state}
            above={
              lang === "es" && (entry.content_en[field.key] ?? "") !== "" ? (
                <p className="whitespace-pre-wrap text-sm text-ink-muted">{entry.content_en[field.key]}</p>
              ) : undefined
            }
          >
            {field.type === "text" ? (
              <Input
                id={`${entry.id}-${field.key}-${lang}`}
                value={fields.value(key)}
                onChange={(e) => fields.set(key, e.target.value)}
                onKeyDown={state.keys}
              />
            ) : (
              // Uncontrolled: remount it to put a discarded or freshly drafted
              // value back into the editor.
              <RichTextField
                key={`${field.key}-${lang}-${state.version}`}
                defaultValue={state.value}
                onChange={(md) => fields.set(key, md)}
              />
            )}
          </InlineField>
        )
      })}

      {aiError && <p className="text-sm text-danger-display">{aiError}</p>}

      {lang === "en" && aiEnabled && !noCopy && (
        <Button type="button" size="sm" variant="secondary" disabled={drafting} onClick={draftWithAi}>
          <Sparkles className="mr-1.5 h-3.5 w-3.5" />
          {drafting ? "Drafting…" : "Draft with AI"}
        </Button>
      )}

      {lang === "en" &&
        entry.placements.map((placement) => (
          <div key={placement.slot_key} className="space-y-2 border-t border-hairline pt-4">
            <p className="text-sm font-medium text-ink-display">{placement.label}</p>
            <PhotoPicker
              slotKey={placement.slot_key}
              siteId={entry.site_id}
              photos={placement.photos}
              library={library}
              maxItems={placement.ranked ? undefined : 1}
              chooseOne={placement.choose_one && settingHolds(placement.choose_one_when) ? {
                selectedPhotoId: placement.selected_photo_id ?? null,
                plainLabel: placement.plain_label ?? "Plain",
              } : undefined}
            />
          </div>
        ))}
    </div>
  )
}
